import React from "react";
import { IoLockClosedOutline, IoCheckmark, IoClose } from "react-icons/io5";
import LookbackPeriodControl from "./LookbackPeriodControl";
import { formatSimpleLookbackRangeLabel } from "./lookbackPeriod";
import {
  GENERIC_NEVER_LINE,
  getConnectorSetupPrimaryLabel,
  getSimpleConnectCopy,
  shouldAutoFinishConnectorSetup,
} from "./businessDigestSetup";

/**
 * Collapsed-first connector setup: icon, privacy copy, primary CTA, and optional advanced sync options.
 */
const SimpleConnectorSetupPanel = ({
  sourceType,
  title,
  compact = false,
  privacySummary = "",
  onboardingLayout = true,
  lookbackDays,
  onLookbackDaysChange,
  lookbackConfig,
  lookbackLoading = false,
  showAdvancedOptions = false,
  onShowAdvancedOptionsChange,
  isGranolaWorkflow = false,
  granolaUseApiKey = false,
  onGranolaUseApiKeyChange,
  granolaApiKey = "",
  onGranolaApiKeyChange,
  onTestGranolaApiKey,
  granolaApiKeyTesting = false,
  granolaApiKeyVerified = false,
  granolaApiKeyTestMessage = "",
  sourceAuthorized = false,
  connectLoading = false,
  submitting = false,
  onConnect,
  onFinish,
  setupFailed = false,
}) => {
  const copy = getSimpleConnectCopy(sourceType);
  const lookbackSummary = formatSimpleLookbackRangeLabel(lookbackDays);
  const granolaOAuthMode = isGranolaWorkflow && !granolaUseApiKey;
  const canFinish = granolaOAuthMode
    ? sourceAuthorized
    : isGranolaWorkflow
      ? granolaApiKeyVerified
      : sourceAuthorized;
  const shouldAutoFinish = shouldAutoFinishConnectorSetup({
    sourceAuthorized,
    isGranolaWorkflow,
    granolaUseApiKey,
    canFinish,
    setupFailed,
  });
  const primaryDisabled =
    submitting ||
    connectLoading ||
    lookbackLoading ||
    shouldAutoFinish ||
    (isGranolaWorkflow && granolaUseApiKey && !granolaApiKeyVerified);

  const handlePrimaryClick = () => {
    if (primaryDisabled) return;
    if (granolaOAuthMode && !sourceAuthorized) {
      onConnect?.();
      return;
    }
    if (!sourceAuthorized && !isGranolaWorkflow) {
      onConnect?.();
      return;
    }
    if (isGranolaWorkflow && granolaUseApiKey && !granolaApiKeyVerified) {
      onTestGranolaApiKey?.();
      return;
    }
    if (canFinish) {
      onFinish?.();
    }
  };

  const primaryLabel = getConnectorSetupPrimaryLabel({
    copy,
    connectLoading,
    submitting,
    sourceAuthorized,
    canFinish,
    shouldAutoFinish,
    isGranolaWorkflow,
    granolaUseApiKey,
    granolaApiKeyVerified,
  });

  const showLockIcon =
    ((!isGranolaWorkflow && !sourceAuthorized) ||
      (granolaOAuthMode && !sourceAuthorized)) &&
    !connectLoading &&
    !submitting;

  return (
    <div
      className={`business-simple-connect${
        compact ? " business-simple-connect--compact" : ""
      }${onboardingLayout ? " business-simple-connect--onboarding" : ""}`}
    >
      {privacySummary ? (
        <div className="connector-setup-privacy-hint">
          <div className="connector-setup-privacy-hint__line">
            <span className="connector-setup-privacy-hint__icon connector-setup-privacy-hint__icon--yes" aria-hidden="true">
              <IoCheckmark />
            </span>
            <span>{copy.accessLine || privacySummary}</span>
          </div>
          {copy.neverLine && copy.neverLine !== GENERIC_NEVER_LINE ? (
            <div className="connector-setup-privacy-hint__line">
              <span className="connector-setup-privacy-hint__icon connector-setup-privacy-hint__icon--no" aria-hidden="true">
                <IoClose />
              </span>
              <span>{copy.neverLine}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {isGranolaWorkflow && granolaUseApiKey ? (
        <div className="business-simple-connect__granola-key business-auth-section">
          <div className="form-input-group">
            <label className="form-label" htmlFor="granola-api-key">
              Granola API key
            </label>
            <textarea
              id="granola-api-key"
              className="form-control"
              rows={4}
              value={granolaApiKey}
              onChange={(event) => onGranolaApiKeyChange?.(event.target.value)}
              placeholder="Paste your Granola API key"
            />
            <div className="business-gmail-actions-row">
              <button
                type="button"
                className="secondary-button"
                onClick={() => void onTestGranolaApiKey?.()}
                disabled={granolaApiKeyTesting || !granolaApiKey.trim()}
              >
                {granolaApiKeyTesting ? "Testing..." : "Test API key"}
              </button>
              {granolaApiKeyVerified ? (
                <div className="business-connected-pill">
                  <span className="business-connected-pill__dot" aria-hidden="true" />
                  API key verified
                </div>
              ) : null}
            </div>
            {granolaApiKeyTestMessage && !granolaApiKeyVerified ? (
              <p className="error-message">{granolaApiKeyTestMessage}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="business-simple-connect__actions">
        {!showAdvancedOptions ? (
          <p className="business-simple-connect__lookback-summary">
            Syncs the last <strong>{lookbackSummary}</strong>.{" "}
            <button
              type="button"
              className="business-simple-connect__change-link"
              onClick={() => onShowAdvancedOptionsChange?.(true)}
            >
              Change
            </button>
          </p>
        ) : (
          <>
            <LookbackPeriodControl
              value={lookbackDays}
              onChange={onLookbackDaysChange}
              quickPresets={lookbackConfig.workflowQuickPresets}
              minDays={lookbackConfig.minDays}
              disabled={lookbackLoading}
            />
            <button
              type="button"
              className="business-simple-connect__change-link business-simple-connect__change-link--collapse"
              onClick={() => onShowAdvancedOptionsChange?.(false)}
            >
              Hide options
            </button>
          </>
        )}

        {sourceAuthorized && (granolaOAuthMode || !isGranolaWorkflow) ? (
          <div className="business-simple-connect__connected">
            <div className="business-connected-pill">
              <span className="business-connected-pill__dot" aria-hidden="true" />
              Connected
            </div>
          </div>
        ) : null}

        <button
          type="button"
          className="primary-button business-simple-connect__cta"
          onClick={() => handlePrimaryClick()}
          disabled={primaryDisabled}
          aria-busy={connectLoading || submitting}
        >
          {connectLoading || submitting ? (
            <span className="business-setup-btn__spinner" aria-hidden="true" />
          ) : showLockIcon ? (
            <IoLockClosedOutline aria-hidden="true" />
          ) : null}
          <span>{primaryLabel}</span>
        </button>

        {isGranolaWorkflow && !granolaUseApiKey ? (
          <button
            type="button"
            className="business-simple-connect__change-link"
            onClick={() => onGranolaUseApiKeyChange?.(true)}
          >
            Use API key instead
          </button>
        ) : null}

        {isGranolaWorkflow && granolaUseApiKey ? (
          <button
            type="button"
            className="business-simple-connect__change-link"
            onClick={() => onGranolaUseApiKeyChange?.(false)}
          >
            {`${copy.connectCta} instead`}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default SimpleConnectorSetupPanel;
